<template>
  <el-dialog
    :model-value="visible"
    width="480"
    @close="emit('close')"
  >
    <template #header>
      <div class="my-header">
        <h4 class="px-2 pb-3 text-xl text-[#344767] font-semibold">
          {{ dialogTitle }}
        </h4>
      </div>
    </template>

    <div class="px-2">
      <div class="text-[#6b7891] text-sm pb-[8px]">
        企业支付密码
      </div>

      <div
        class="custom-input-wrapper pl-4"
        :class="{ 'custom-field-error': errors.payPassword }"
      >
        <input
          v-model="formState.payPassword"
          type="password" 
          placeholder="请输入支付密码"
        >
        <span class="custom-field-error-span">{{ errors.payPassword }}</span>
      </div>

      <div class="text-[#6b7891] text-sm pb-[8px]">
        支付手机号
      </div>

      <div
        class="custom-input-wrapper pl-4 disabled"
        :class="{ 'custom-field-error': errors.phone }"
      >
        <input
          type="text"
          :value="formState.phone"
          placeholder="请输入11位手机号码"
          disabled
          @change="onPhoneChange"
          @keyup.enter="onConfirm"
        >
        <span class="custom-field-error-span">{{ errors.phone }}</span>
      </div>

      <div class="text-[#6b7891] text-sm pb-[8px]">
        验证码
      </div>

      <div class="relative">
        <div
          class="custom-input-wrapper !w-[318px] mb-4"
          :class="{ 'custom-field-error': errors.verifyCode }"
        >
          <svg-icon icon="lucide:shield-check" />
          <input
            type="text"
            :value="formState.verifyCode"
            placeholder="请输入验证码"
            @change="onSmscodeChange"
            @keyup.enter="onConfirm"
          >
        </div>

        <sms-button
          ref="smsBtnRef"
          class="!left-[331px]"
          @click="sendSms"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-center">
        <el-button @click="emit('close')">
          取消
        </el-button>
        <el-button type="primary" :loading="loading" @click="onConfirm">
          确定
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ESmsType, sendSmscode } from '@core/api';
import { checkPhone, checkSmsCode } from '@core/tools';
import { SvgIcon, SmsButton } from '@vue3/components';
import { ElMessage } from 'element-plus';
import { computed, reactive, ref, watchEffect } from 'vue';

let sendSmsLock = false;

const emit = defineEmits(['close', 'confirm']);

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  phone: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const dialogTitle = computed(() => props.title);

const formState = reactive({
  payPassword: '',
  phone: '',
  verifyCode: '',
});

const errors = reactive({
  payPassword: '',
  phone: '',
  verifyCode: '',
});

const smsBtnRef = ref<InstanceType<typeof SmsButton>>(null);

const onConfirm = async () => {
  if (errors.payPassword) return;
  if (errors.phone) return;
  if (errors.verifyCode) return;

  if (!formState.payPassword) return ElMessage.error('请输入支付密码');
  if (!formState.phone) return ElMessage.error('请输入手机号');
  if (!formState.verifyCode) return ElMessage.error('请输入验证码');

  emit('confirm', JSON.parse(JSON.stringify(formState)));
};

async function sendSms() {
  if (sendSmsLock) return;

  try {
    sendSmsLock = true;
    smsBtnRef.value?.showLoading();
    await sendSmscode(ESmsType.SplitBill, props.phone);

    smsBtnRef.value?.countdown();
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);

    smsBtnRef.value?.hideLoading();
  }

  setTimeout(() => {
    sendSmsLock = false;
  }, 1300);
}

function onSmscodeChange(e: Event) {
  const target = e.target as HTMLInputElement;
  const err = checkSmsCode(target.value);

  errors.verifyCode = err;
  formState.verifyCode = target.value;
}

function onPhoneChange(e: Event) {
  const target = e.target as HTMLInputElement;
  let err = checkPhone(target.value);

  errors.phone = err;
  formState.phone = target.value;
}

watchEffect(() => {
  if (props.visible) {
    formState.payPassword = '';
    formState.phone = props.phone;
    formState.verifyCode = '';
    errors.payPassword = '';
    errors.phone = '';
    errors.verifyCode = '';
    sendSmsLock = false;
    smsBtnRef.value?.hideLoading();
  }
});
</script>
